import React, { useEffect, useState, useMemo } from 'react';
import { Modal, List, Tag, Space, Typography, Button, Tooltip, Empty, Spin, Pagination, Divider } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getEvents } from '../../api/events';
import { useChecklistsState } from '../../state/checklistsState';

const { Text } = Typography;

const typeColor = {
	CREATE: 'green',
	UPDATE: 'blue',
	DELETE: 'red',
	COPY: 'purple'
};

const objectLabels = {
	CHECKLIST: 'Чеклист',
	CUSTOM_FIELD: 'Кастомное поле',
	CUSTOM_FIELD_STATUS: 'Связь поля-статуса'
};

const formatDate = (iso) => {
	try {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return iso || '';
		return d.toLocaleString();
	} catch (e) {
		return iso || '';
	}
};

const EventsModal = ({ open, onClose, leadId }) => {
	const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const { checklists } = useChecklistsState?.() || { checklists: [] };

    const fieldIdToName = useMemo(() => {
        const map = new Map();
        try {
            (checklists || []).forEach((cl) => {
                (cl.custom_fields || []).forEach((cf) => {
                    if (cf && typeof cf.id === 'number') {
                        map.set(cf.id, cf.name);
                    }
                });
            });
        } catch (e) { /* ignore */ }
        return map;
    }, [checklists]);

    const loadViaApi = async (nextPage = page, nextPageSize = pageSize) => {
        if (!leadId) return;
        setLoading(true);
        try {
            // наш API клиент пока возвращает массив без пагинации, поэтому делаем ручную пагинацию на фронте, если backend не вернул count
            const data = await getEvents({ leadId, page: nextPage, page_size: nextPageSize });
            if (data && typeof data === 'object' && 'results' in data) {
                setEvents(Array.isArray(data.results) ? data.results : []);
                setTotal(typeof data.count === 'number' ? data.count : (Array.isArray(data.results) ? data.results.length : 0));
            } else if (Array.isArray(data)) {
                setTotal(data.length);
                const start = (nextPage - 1) * nextPageSize;
                setEvents(data.slice(start, start + nextPageSize));
            } else {
                setEvents([]);
                setTotal(0);
            }
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
            loadViaApi(1, pageSize);
            setPage(1);
		}
    }, [open, leadId]);

	return (
		<Modal
			open={open}
            title={
                <Space>
                    События сделки
                    <Text type="secondary">#{leadId}</Text>
                    <Tooltip title="Обновить">
                        <Button size="small" icon={<ReloadOutlined />} onClick={() => loadViaApi(page, pageSize)} disabled={loading} />
                    </Tooltip>
                </Space>
            }
			onCancel={onClose}
			footer={null}
			width={720}
			destroyOnClose
		>
			{loading ? (
				<div style={{ textAlign: 'center', padding: 24 }}>
					<Spin />
				</div>
			) : events.length === 0 ? (
				<Empty description="Нет событий по этой сделке" />
            ) : (
                <>
                <List
					dataSource={events}
					renderItem={(ev) => {
						const details = ev.details || {};
						const userInfo = ev.user_info || {};
						return (
							<List.Item>
								<Space direction="vertical" style={{ width: '100%' }} size={2}>
									<Space wrap align="center">
										<Tag color={typeColor[ev.event_type] || 'default'}>{ev.event_type}</Tag>
										<Tag>{objectLabels[ev.object_type] || ev.object_type}</Tag>
										<Text type="secondary">{formatDate(ev.created_at)}</Text>
									</Space>
									<Space wrap>
										{typeof ev.object_id !== 'undefined' && (
											<Text>Объект: #{ev.object_id}</Text>
										)}
										{typeof details.checklist_id !== 'undefined' && (
											<Text>Чеклист: #{details.checklist_id}</Text>
										)}
										{typeof details.lead_id !== 'undefined' && (
											<Text>Сделка: #{details.lead_id}</Text>
										)}
										{userInfo.name && (
											<Text type="secondary">Пользователь: {decodeURIComponent(userInfo.name)}</Text>
										)}
									</Space>
                                    <DetailsBlock details={details} fieldIdToName={fieldIdToName} />
								</Space>
							</List.Item>
						);
					}}
				/>
                <Divider style={{ margin: '8px 0' }} />
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    onChange={(p, ps) => { setPage(p); setPageSize(ps); loadViaApi(p, ps); }}
                    size="small"
                    showSizeChanger
                    showTotal={(t) => `Всего: ${t}`}
                />
                </>
			)}
		</Modal>
	);
};

const DetailsBlock = ({ details, fieldIdToName }) => {
    if (!details || typeof details !== 'object') return null;

    const blocks = [];
    if (details.lead_id) blocks.push(<Text key="lead">Сделка: #{details.lead_id}</Text>);
    if (details.checklist_id) blocks.push(<Text key="cl">Чеклист: #{details.checklist_id}</Text>);
    if (details.custom_field_id) {
        const name = fieldIdToName?.get?.(details.custom_field_id);
        blocks.push(
            <Text key="cf">
                Поле: {name ? `${name} (#${details.custom_field_id})` : `#${details.custom_field_id}`}
            </Text>
        );
    }

    const hasChange = details.old_content !== undefined || details.new_content !== undefined;

    const renderValue = (val) => {
        if (val === null || val === undefined) return 'Не выбрано';
        if (typeof val === 'boolean') return val ? 'Включено' : 'Выключено';
        if (Array.isArray(val)) return val.length ? val.join(', ') : 'Не выбрано';
        return String(val);
    };

    const renderChange = (oldC, newC) => {
        // Определяем тип содержимого
        if (oldC && typeof oldC === 'object' && 'active' in oldC || newC && typeof newC === 'object' && 'active' in newC) {
            const oldV = oldC ? oldC.active : undefined;
            const newV = newC ? newC.active : undefined;
            return (
                <>
                    <div><Text type="secondary">Было:</Text> {renderValue(oldV)}</div>
                    <div><Text type="secondary">Стало:</Text> {renderValue(newV)}</div>
                </>
            );
        }
        if (oldC && typeof oldC === 'object' && 'value' in oldC || newC && typeof newC === 'object' && 'value' in newC) {
            const oldV = oldC ? oldC.value : undefined;
            const newV = newC ? newC.value : undefined;
            return (
                <>
                    <div><Text type="secondary">Было:</Text> {renderValue(oldV)}</div>
                    <div><Text type="secondary">Стало:</Text> {renderValue(newV)}</div>
                </>
            );
        }
        if (oldC && typeof oldC === 'object' && 'values' in oldC || newC && typeof newC === 'object' && 'values' in newC) {
            const oldV = oldC ? oldC.values : undefined;
            const newV = newC ? newC.values : undefined;
            return (
                <>
                    <div><Text type="secondary">Было:</Text> {renderValue(oldV)}</div>
                    <div><Text type="secondary">Стало:</Text> {renderValue(newV)}</div>
                </>
            );
        }
        // Фоллбэк: аккуратно показать JSON
        return (
            <>
                {oldC !== undefined && (
                    <div>
                        <Text type="secondary">Было:</Text>
                        <pre style={{ margin: 0 }}>{JSON.stringify(oldC, null, 2)}</pre>
                    </div>
                )}
                {newC !== undefined && (
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">Стало:</Text>
                        <pre style={{ margin: 0 }}>{JSON.stringify(newC, null, 2)}</pre>
                    </div>
                )}
            </>
        );
    };

    return (
        <div style={{ marginTop: 6 }}>
            {blocks.length > 0 && (
                <Space wrap size={8} style={{ marginBottom: 4 }}>
                    {blocks}
                </Space>
            )}
            {hasChange && (
                <div>
                    {renderChange(details.old_content, details.new_content)}
                </div>
            )}
            {!hasChange && Object.keys(details).length > 0 && (
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(details, null, 2)}
                </pre>
            )}
        </div>
    );
};

export default EventsModal;


